import React, { useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useForm, Controller } from 'react-hook-form';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import { getFileClients, shareFile } from 'services/fileManager/fileManagerServices';
import { revokeFileAccess } from 'modules/file_manager/services';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import GlobalTooltip from 'components/GlobalTooltip';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';

function payloadData(item, contactIds) {
  const contactIdsList = Array.isArray(contactIds) ? contactIds : [contactIds];

  const clientData = {
    contact_id_list: contactIdsList,
  };

  if (item.entry_type === 'folder') {
    clientData.folder_list = [{ folder_pk: item.pk, folder_sk: item.sk }];
  } else {
    clientData.file_list = [{ file_pk: item.pk, file_sk: item.sk }];
  }

  return clientData;
}

function ShareModal() {
  const { modals, isLeadFileManager, selectedRows, setSelectedRows, setFileManagerFilters } = useFileManagerContext();
  const { showShareFileModal, setShowShareFileModal } = modals;
  const { addToast } = useToast();

  const folderCount = selectedRows.filter((item) => item.entry_type === 'folder').length;
  const fileCount = selectedRows.filter((item) => item.entry_type === 'file').length;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const [clientOptions, setClientOptions] = React.useState([]);
  const [sharedContacts, setSharedContacts] = React.useState([]);

  const itemHasSharedAccess = useCallback(
    (item) =>
      Boolean(item?.file_shared_contact_id?.length) || sharedContacts.some((contact) => contact.display_name === item?.display_name),
    [sharedContacts]
  );

  const handleRemoveAccess = useCallback(
    (item) => {
      if (!item?.case_id || !itemHasSharedAccess(item)) {
        addToast(false, toastConstant.toasterType.WARN, toastConstant.api.ERROR, 'This item has no shared access to remove.');
        return;
      }

      const ids = item?.file_shared_contact_id?.length ? item.file_shared_contact_id : [];
      if (!ids.length) {
        addToast(false, toastConstant.toasterType.WARN, toastConstant.api.ERROR, 'This item has no shared access to remove.');
        return;
      }

      const clientData = payloadData(item, ids);

      revokeFileAccess(item.case_id, isLeadFileManager, clientData)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Access removed successfully.');
          setSharedContacts((prev) => prev.filter((contact) => contact.display_name !== item.display_name));
          setSelectedRows((prev) =>
            prev.map((row) => (row.pk === item.pk && row.sk === item.sk ? { ...row, file_shared_contact_id: [] } : row))
          );
          setFileManagerFilters((prev) => ({ ...prev, refresh: true }));
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to remove access.');
        });
    },
    [addToast, isLeadFileManager, itemHasSharedAccess, setFileManagerFilters, setSelectedRows, setShowShareFileModal]
  );

  const handleRemoveSharedUser = useCallback(
    (item, assignee) => {
      if (!item?.case_id) {
        return;
      }

      const contactId = assignee.contact_id || assignee.value;
      if (!contactId) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Could not remove this contact.');
        return;
      }

      const clientData = payloadData(item, [contactId]);

      revokeFileAccess(item.case_id, isLeadFileManager, clientData)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Access removed for this contact.');
          setSharedContacts((prev) =>
            prev.filter(
              (c) => !(c.display_name === item.display_name && (c.contact_id === assignee.contact_id || c.value === assignee.value))
            )
          );
          setSelectedRows((prev) =>
            prev.map((row) => {
              if (row.pk !== item.pk || row.sk !== item.sk) {
                return row;
              }
              const nextIds = (row.file_shared_contact_id || []).filter((id) => id !== assignee.contact_id && id !== assignee.value);
              return { ...row, file_shared_contact_id: nextIds };
            })
          );
          setFileManagerFilters((prev) => ({ ...prev, refresh: true }));
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to remove access for this contact.');
        });
    },
    [addToast, isLeadFileManager, setFileManagerFilters, setSelectedRows]
  );

  // Check if we have folders selected
  const hasFolders = selectedRows?.some((item) => item.entry_type === 'folder');
  const hasFiles = selectedRows?.some((item) => item.entry_type === 'file');
  const onlyFolders = hasFolders && !hasFiles;
  const mixedSelection = hasFolders && hasFiles;

  useEffect(() => {
    let caseId = selectedRows?.[0]?.case_id ? selectedRows?.[0]?.case_id : '';
    fetchClients(caseId);
  }, [selectedRows]);

  useEffect(() => {
    if (selectedRows && selectedRows.length > 0 && clientOptions.length > 0) {
      const defaultAssigneeList = selectedRows.reduce((acc, row) => {
        if (row.file_shared_contact_id) {
          row.file_shared_contact_id.forEach((fileContactId) => {
            const foundClient = clientOptions.find((client) => client.contact_id === fileContactId);
            if (foundClient) {
              acc.push({
                ...foundClient,
                display_name: row.display_name,
              });
            }
          });
        }
        return acc;
      }, []);
      setSharedContacts(defaultAssigneeList);
    }
  }, [clientOptions, selectedRows]);

  const fetchClients = (caseId = '') => {
    let obj = {
      case_id: caseId,
    };
    getFileClients(obj)
      .then((response) => {
        if (response && response.data && response.data.length) {
          let data = [];
          response.data.forEach((element) => {
            let label = generateFullName(element);
            data.push({
              label: label,
              value: element?.client_cognito_username ? element?.client_cognito_username : element?.contact_id,
              email: element?.email,
              is_create_client_portal: element?.is_create_client_portal,
              contact_id: element?.contact_id ? element?.contact_id : '',
              file_name: element?.display_name ? element?.display_name : '',
            });
          });
          setClientOptions([...data]);
        } else {
          setClientOptions([]);
        }
      })
      .catch(() => {
        setClientOptions([]);
      });
  };

  const shareItemsWithClient = (data) => {
    if (selectedRows?.length > 0) {
      // Separate files and folders
      const files = selectedRows.filter((item) => item.entry_type === 'file');
      const folders = selectedRows.filter((item) => item.entry_type === 'folder');

      if (folders.length > 0 || files.length > 0) {
        // Create unified sharing data with both files and folders
        let unifiedShareData = {
          client_cognito_username: data.assignee,
        };

        // Add file list if there are files
        if (files.length > 0) {
          unifiedShareData.file_list = files.map((row) => ({ file_pk: row.pk, file_sk: row.sk }));
        }

        // Add folder list if there are folders
        if (folders.length > 0) {
          unifiedShareData.folder_list = folders.map((row) => ({ folder_pk: row.pk, folder_sk: row.sk }));
        }

        let shareObj = {
          case_id: selectedRows[0]?.case_id,
          data: unifiedShareData,
          is_lead: isLeadFileManager,
        };

        // Use single shareFile call for both files and folders
        shareFile(shareObj)
          .then(() => {
            const itemType = onlyFolders ? 'FOLDERS' : mixedSelection ? 'ITEMS' : 'FILES';
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, `${itemType} shared successfully`);
            setShowShareFileModal(false);
            setSelectedRows([]);
            setFileManagerFilters((prev) => ({ ...prev, refresh: true }));
          })
          .catch((error) => {
            console.log(error);
            const itemType = onlyFolders ? 'FOLDERS' : mixedSelection ? 'ITEMS' : 'FILES';
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, `Failed to share ${itemType.toLowerCase()}`);
          });
      }
    }
  };

  function checkOptionDisabled(index) {
    if (index.email === '' || !index.is_create_client_portal) {
      return true;
    }
    return false;
  }

  function ClientsOptionTemplate(e) {
    return (
      <div>
        {e.label} {!e.email ? '( Email address not found )' : !e.is_create_client_portal ? '( Lawft Access Disabled )' : ''}
      </div>
    );
  }

  const customFooter = () => {
    return (
      <div className="footer-btn">
        <Button label="Cancel" onClick={() => setShowShareFileModal(false)} className="p-button p-button-secondary me-3 outline" />
        <Button label="Share" className="p-button p-button-secondary" onClick={handleSubmit(shareItemsWithClient)} autoFocus />
      </div>
    );
  };

  const getModalTitle = () => {
    if (onlyFolders) {
      return selectedRows.length === 1 ? 'Share Folder' : 'Share Folders';
    } else if (mixedSelection) {
      return 'Share Items';
    } else {
      return selectedRows.length === 1 ? 'Share File' : 'Share Files';
    }
  };

  const getModalHeader = (data) => {
    return (
      <div className="d-flex align-items-center justify-content-between">
        <i className="icon-window-filled modal-close-icon pointer F-size14" onClick={data?.onHide} />
      </div>
    );
  };

  return (
    <Dialog
      className="new-task-popup"
      onHide={() => setShowShareFileModal(false)}
      header={getModalHeader}
      footer={customFooter}
      title={getModalTitle()}
      visible={showShareFileModal}
      style={{ width: '40vw' }}
    >
      <div className="mb-3 p-medium">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span className="modal-heading F-size16 text-bold">Share File</span>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {fileCount > 0 && (
              <span className="d-inline-flex align-items-center">
                <i className="far fa-file me-2 text-primary lh-1" aria-hidden />
                <span>
                  {fileCount} File{fileCount !== 1 ? 's' : ''}
                </span>
              </span>
            )}
            {fileCount > 0 && folderCount > 0 && <span className="text-bold">•</span>}
            {folderCount > 0 && (
              <span className="d-inline-flex align-items-center">
                <i className="icon-folder fa-lg me-2 lh-1" aria-hidden />
                <span>
                  {folderCount} Folder{folderCount !== 1 ? 's' : ''}
                </span>
              </span>
            )}
            <span>Selected</span>
          </div>
        </div>

        <span>
          {hasFolders && (
            <div className="text-muted small mt-2">
              <i className="fas fa-info-circle me-1"></i>
              Sharing folders will also share all files and subfolders within them.
            </div>
          )}
        </span>
      </div>

      <div className="d-flex align-items-center flex-wrap">
        <div className="col-lg-4 col-12 p-medium">Share with </div>
        <div className="col-lg-8 col-12 select-tag-input">
          <Controller
            name="assignee"
            control={control}
            defaultValue={[]}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <MultiSelect
                  options={clientOptions}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select Contact (s)"
                  className="input-shadow w-100 F-size14"
                  filter
                  id={field.name}
                  value={field.value}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  emptyFilterMessage="No records found"
                  optionDisabled={(index) => checkOptionDisabled(index)}
                  itemTemplate={ClientsOptionTemplate}
                />
              </>
            )}
          />
          {errors?.assignee && <InputErrorMessage>Required</InputErrorMessage>}
        </div>
        <span className="border-bottom pb-3 w-100 mb-3"></span>
        <div className="pb-3 w-100">
          <div className="caption-bold mt-4">Shared with</div>
          <Card className="card-bg p-2">
            {selectedRows?.map((item, index) => (
              <div key={index}>
                <div className="p-medium d-flex align-items-center justify-content-between ">
                  <div className="d-flex align-items-center">
                    {item.entry_type === 'folder' ? (
                      <i className="icon-folder fa-lg me-2"></i>
                    ) : (
                      <i className="far fa-file me-2 text-primary"></i>
                    )}
                    <span>
                      {item.entry_type === 'folder' ? 'Folder' : 'File'}: {item.display_name}
                    </span>
                  </div>
                  <>
                    {sharedContacts.filter((contact) => contact.display_name === item.display_name).length > 0 && (
                      <span role="button" tabIndex={0} className="cursor-pointer" onClick={() => handleRemoveAccess(item)}>
                        <GlobalTooltip>
                          <span className="text-danger">Remove access for all</span>
                        </GlobalTooltip>
                      </span>
                    )}
                  </>
                </div>
                <div className="col-12 mb-3 mt-2 d-flex flex-wrap align-items-center gap-2">
                  {sharedContacts
                    .filter((contact) => contact.display_name === item.display_name)
                    .map((assignee) => {
                      return (
                        <Tag className="tag tag-grey fw-normal badge-pill px-2">
                          <span className="text-truncate" style={{ maxWidth: '220px' }}>
                            {assignee.label}
                          </span>
                          <i className="pi pi-times-circle ms-2" aria-hidden onClick={() => handleRemoveSharedUser(item, assignee)} />
                        </Tag>
                      );
                    })}
                  {sharedContacts.filter((contact) => contact.display_name === item.display_name).length === 0 && (
                    <div className="d-flex align-items-center">
                      <i className="icon icon-Alert me-2 cursor-pointer F-size16"></i>
                      <span className="text-muted">{item.entry_type === 'folder' ? 'Folder' : 'File'} not shared</span>
                    </div>
                  )}
                </div>
                {index < (selectedRows?.length ?? 0) - 1 && <div className="border-bottom w-100 mb-3 pb-3" aria-hidden />}
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Dialog>
  );
}

export default ShareModal;
